import { permissionsService } from '../../services/permissions.service'
import { settingsService } from '../../services/settings.service'

export const WORKFLOW_TASK_LIST_COLUMNS = [
  'document',
  'workflow',
  'workflow_status',
  'step',
  'status',
  'assigned',
  'created',
] as const

export type WorkflowTaskListColumnId = (typeof WORKFLOW_TASK_LIST_COLUMNS)[number]

export const DEFAULT_WORKFLOW_TASK_LIST_COLUMNS: WorkflowTaskListColumnId[] = [...WORKFLOW_TASK_LIST_COLUMNS]

const CONFIG_BASE = 'workflow-task-list:currentViewConfig'

function resolveOrganizationId(explicit: number | null | undefined): number | null {
  if (explicit !== undefined) {
    return explicit
  }
  return settingsService.getCurrentOrganizationId()
}

function resolveUserId(explicit: number | null | undefined): number | null {
  if (explicit !== undefined) {
    return explicit
  }
  return permissionsService.getCurrentUser()?.id ?? null
}

function getViewConfigStorageKey(userId: number | null, organizationId: number | null): string {
  if (userId != null && organizationId != null) {
    return `${CONFIG_BASE}:u${userId}:o${organizationId}`
  }
  if (userId != null) {
    return `${CONFIG_BASE}:u${userId}`
  }
  if (organizationId != null) {
    return `${CONFIG_BASE}:o${organizationId}`
  }
  return CONFIG_BASE
}

function isValidColumn(id: string): id is WorkflowTaskListColumnId {
  return (WORKFLOW_TASK_LIST_COLUMNS as readonly string[]).includes(id)
}

function normalizeColumnsFromArray(raw: unknown): WorkflowTaskListColumnId[] | null {
  if (!Array.isArray(raw) || raw.length === 0) {
    return null
  }
  const filtered = raw.filter((x): x is WorkflowTaskListColumnId => typeof x === 'string' && isValidColumn(x))
  if (filtered.length === 0) {
    return null
  }
  return WORKFLOW_TASK_LIST_COLUMNS.filter((c) => filtered.includes(c))
}

export interface WorkflowTaskListViewConfig {
  displayColumns: WorkflowTaskListColumnId[]
}

function readColumnsForKey(key: string): WorkflowTaskListColumnId[] | null {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null
  }
  const raw = window.localStorage.getItem(key)
  if (!raw) {
    return null
  }
  try {
    const parsed = JSON.parse(raw) as unknown
    if (parsed && typeof parsed === 'object' && 'displayColumns' in parsed) {
      return normalizeColumnsFromArray((parsed as WorkflowTaskListViewConfig).displayColumns)
    }
  } catch {
    return null
  }
  return null
}

export function loadWorkflowTaskListColumns(
  organizationId?: number | null,
  userId?: number | null,
): WorkflowTaskListColumnId[] {
  if (typeof window === 'undefined' || !window.localStorage) {
    return [...DEFAULT_WORKFLOW_TASK_LIST_COLUMNS]
  }
  const org = resolveOrganizationId(organizationId)
  const user = resolveUserId(userId)
  const key = getViewConfigStorageKey(user, org)
  return readColumnsForKey(key) ?? [...DEFAULT_WORKFLOW_TASK_LIST_COLUMNS]
}

export function saveWorkflowTaskListColumns(
  cols: WorkflowTaskListColumnId[],
  organizationId?: number | null,
  userId?: number | null,
): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return
  }
  const org = resolveOrganizationId(organizationId)
  const user = resolveUserId(userId)
  const key = getViewConfigStorageKey(user, org)
  const payload: WorkflowTaskListViewConfig = { displayColumns: cols }
  try {
    window.localStorage.setItem(key, JSON.stringify(payload))
  } catch {
    // ignore quota / private mode
  }
}
